import { readAuditFixturesFlag } from '../../utils/auditFixtures';
import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, Pressable } from 'react-native';
import {
  Check,
  Map as MapIcon,
  Satellite,
  Camera,
  Flame,
  History,
  Presentation,
  Shield,
  Route,
  MapPin,
  ImagePlus,
  Pencil,
  Trash2,
  Plus,
  Award,
  Users,
  X,
} from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, FONT_SIZES, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useUIStore, useObservationsVisible, type MapStyle } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useMapOverlaysStore } from '../../store/mapOverlaysStore';
import { useAuthStore } from '../../store/authStore';
import { mapOverlaysApi } from '../../api/mapOverlays';
import { useAuthGate } from '../../hooks/useAuthGate';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { scheduleDeferredCommit } from '../../utils/deferredCommit';
import { FHSZ_SOURCE_LABEL } from './fhszColors';
import { FIRE_HISTORY_SOURCE_LABEL } from './fireHistoryColors';
import { COMMUNITY_PROGRAMS } from './communityPrograms';
import type { MapOverlay } from '../../types';

// The audits' own duplicate of a real map (scripts/seed-plan-maps.ts,
// scripts/audit-visual.mjs's PLAN_MAP_FIXTURE_ID) — shown under a live map
// of the same imagery, so a normal session hides it. Audit pages set the
// flag once via localStorage before the app boots (createNewPage in
// scripts/lib/web-audit-helpers.mjs); read once here, not subscribed, since
// it never changes during a session.
const PLAN_MAP_FIXTURE_ID = 'audit-fixture-overlay';

// Numeric-aware, case-insensitive — "Map 2.10" sorts after "Map 2.9", not
// before it, and a lowercase title doesn't sort separately from an
// uppercase one. Plain localeCompare(undefined, {numeric, sensitivity})
// rather than a hand-rolled parse. Plan map titles only — the reorder-after-
// every-save bug this fixes is specific to that list.
function compareTitles(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

interface BasemapOption {
  id: MapStyle;
  label: string;
  description: string;
  icon: LucideIcon;
}

const BASEMAP_OPTIONS: BasemapOption[] = [
  { id: 'standard', label: 'Standard', description: 'Illustrated street map', icon: MapIcon },
  {
    id: 'satellite',
    label: 'Satellite',
    description: 'Aerial imagery with roads and place names',
    icon: Satellite,
  },
];

/**
 * Everything drawn on the map, in one place: which basemap is under it and
 * which overlays are on top.
 *
 * It replaced two icon buttons in the control stack that each flipped
 * something without saying what it was or which way it was set. A list with
 * labels and source lines costs one tap and answers both.
 *
 * Overlays are web only — the Leaflet hooks that draw them have no native
 * counterpart — so on phones this is a basemap picker and nothing else.
 *
 * Pure content, no presentation chrome: `MapLayersSheet` wraps this in the
 * phone gorhom sheet, `MapLayersPanel.web` wraps it in the desktop-shell
 * left-docked panel. The root is a bare `View` so either wrapper owns
 * scrolling and sizing.
 */
export function MapLayersContent({
  onClose,
  showCloseButton = false,
  onImportMap,
}: {
  onClose: () => void;
  /** True in the desktop-shell docked panel, which has no sheet chrome of
   *  its own to close it — an in-content X row does that job instead.
   *  False in the phone sheet, which already has its own dismiss (scrim tap,
   *  swipe, hardware back). */
  showCloseButton?: boolean;
  /** Desktop shell only (MapLayersPanel.web.tsx): pushes PlanMapImportForm
   *  into this panel's own PanelStack instead of opening the global
   *  `planMapImport` sheet (panel-system.md Phase 2 — a drill-in, not a
   *  second dock). Omit to keep the sheet-opening default (phone). */
  onImportMap?: () => void;
}) {
  const { colors } = useTheme();
  const openSheet = useUIStore((s) => s.openSheet);
  const mapStyle = useUIStore((s) => s.mapStyle);
  const setMapStyle = useUIStore((s) => s.setMapStyle);
  const fhszOverlayEnabled = useUIStore((s) => s.fhszOverlayEnabled);
  const toggleFhszOverlay = useUIStore((s) => s.toggleFhszOverlay);
  const fireHistoryEnabled = useUIStore((s) => s.fireHistoryEnabled);
  const toggleFireHistory = useUIStore((s) => s.toggleFireHistory);
  const firewiseEnabled = useUIStore((s) => s.firewiseEnabled);
  const toggleFirewise = useUIStore((s) => s.toggleFirewise);
  const fireSafeCouncilsEnabled = useUIStore((s) => s.fireSafeCouncilsEnabled);
  const toggleFireSafeCouncils = useUIStore((s) => s.toggleFireSafeCouncils);
  const observationsVisible = useObservationsVisible();
  const setObservationsVisible = useUIStore((s) => s.setObservationsVisible);
  const requestSlideshowOpen = useUIStore((s) => s.requestSlideshowOpen);
  const requestPlanPanelOpen = useUIStore((s) => s.requestPlanPanelOpen);
  const closePlanPanel = useUIStore((s) => s.closePlanPanel);
  const setViewedPlanId = useUIStore((s) => s.setViewedPlanId);
  const requestMapPlanFocus = useUIStore((s) => s.requestMapPlanFocus);
  // Plan maps (Sept 2026 phase 2) — moderator-imported wildfire plan pages
  // placed on the map. `overlays` already carries only what this session is
  // allowed to see (shared-only for a non-moderator, see mapOverlaysStore's
  // subscribe) — no extra filtering needed here.
  const planMapOverlays = useMapOverlaysStore((s) => s.overlays);
  const hiddenOverlayIds = useMapOverlaysStore((s) => s.hiddenOverlayIds);
  const hideOverlay = useMapOverlaysStore((s) => s.hideOverlay);
  const unhideOverlay = useMapOverlaysStore((s) => s.unhideOverlay);
  const isModerator = useAuthStore((s) => s.isModerator);
  const enabledOverlayIds = useUIStore((s) => s.enabledOverlayIds);
  const toggleOverlay = useUIStore((s) => s.toggleOverlay);
  const setOverlayEnabled = useUIStore((s) => s.setOverlayEnabled);
  const startOverlayPlacement = useUIStore((s) => s.startOverlayPlacement);
  // A plan whose last feature was just deleted keeps its doc (undo needs the
  // id — see defensePlansStore.removeFeature), so it can show up here with
  // nothing in it. Filter it out: "Shared plans" is for plans someone can
  // actually open and see something on the map. Filtered with useMemo, not
  // inline in the selector — a selector has to return a referentially
  // stable value when the underlying state hasn't changed (React's
  // useSyncExternalStore requires it), and `.filter()` returns a new array
  // every call. Filtering inside the selector crashed the whole app with
  // "Maximum update depth exceeded" (React error #185) the first time this
  // shipped: every store notification looked like a change, forever.
  const rawAreaPlans = useDefensePlansStore((s) => s.areaPlans);
  // The signed-in user's own plans (Sept 2026 multi-plan work), newest-first
  // by updatedAt — src/api/defensePlans.ts's subscribeOwnPlans sorts them,
  // so the first entry is already "most recently updated" with no re-sort
  // needed here.
  const ownPlans = useDefensePlansStore((s) => s.ownPlans);
  const setActiveOwnPlan = useDefensePlansStore((s) => s.setActiveOwnPlan);
  const startNewPlan = useDefensePlansStore((s) => s.startNewPlan);
  const ownPlanIds = useMemo(() => new Set(ownPlans.map((p) => p.id)), [ownPlans]);
  // "Shared plans" excludes the viewer's own — those have their own group
  // above — and the same empty-tombstone filter areaPlans always needed
  // (see the comment on rawAreaPlans's old single-list version, kept
  // below).
  const sharedPlans = useMemo(
    () => rawAreaPlans.filter((p) => p.featureCount > 0 && !ownPlanIds.has(p.id)),
    [rawAreaPlans, ownPlanIds]
  );
  const isDesktopShell = useDesktopShell();
  const { requireAuth } = useAuthGate();
  // Read once — the audits set this before the app boots (createNewPage in
  // scripts/lib/web-audit-helpers.mjs) and it never changes mid-session, so
  // there's nothing to subscribe to.
  const [showAuditFixtures] = useState(readAuditFixturesFlag);

  const handlePresentObservations = () => {
    onClose();
    requestSlideshowOpen();
  };

  const handleDrawPlan = () => {
    onClose();
    requireAuth(() => requestPlanPanelOpen(), { message: 'Sign in to draw a defense plan' });
  };

  const handleOpenOwnPlan = (planId: string) => {
    onClose();
    setActiveOwnPlan(planId);
    requestPlanPanelOpen();
  };

  const handleNewPlan = () => {
    onClose();
    startNewPlan();
    requestPlanPanelOpen();
  };

  const handleOpenAreaPlan = (planId: string) => {
    onClose();
    closePlanPanel();
    setViewedPlanId(planId);
    requestMapPlanFocus(planId);
  };

  const handleAddPlanMap = () => {
    if (onImportMap) {
      onImportMap();
      return;
    }
    // Swaps this sheet's own slot for the import sheet — both are
    // `uiStore.activeSheet` values, so no separate onClose() call is needed.
    openSheet('planMapImport');
  };

  const handleEditPlanMapPlacement = (overlayId: string) => {
    onClose();
    setOverlayEnabled(overlayId, true);
    startOverlayPlacement(overlayId);
  };

  // Removes the plan map immediately (it disappears from this list, and from
  // the map if it was on) and commits the real delete six seconds later
  // unless Undo cancels it — replaces the "Delete this plan map?" confirm
  // (panel-system.md Phase 4, rule 6: an undo toast instead of a confirm for
  // a reversible action).
  const handleRemovePlanMap = (overlay: MapOverlay) => {
    scheduleDeferredCommit({
      key: `overlay-${overlay.id}`,
      message: `Removed ${overlay.title}`,
      commit: async () => {
        await mapOverlaysApi.deleteOverlay(overlay);
        unhideOverlay(overlay.id);
      },
      onHide: () => hideOverlay(overlay.id),
      onUndo: () => unhideOverlay(overlay.id),
    });
  };

  const showOverlays = Platform.OS === 'web';
  // Hide the audits' own duplicate map (PLAN_MAP_FIXTURE_ID) from a normal
  // session — it exists in dev only so the visual/interaction suites have a
  // fixture that never reorders or gets edited out from under them. Sorted
  // by title, numeric-aware — the id order reshuffled every time a
  // moderator saved a title edit.
  const visiblePlanMapOverlays = useMemo(
    () =>
      [...planMapOverlays]
        .filter(
          (o) => (showAuditFixtures || o.id !== PLAN_MAP_FIXTURE_ID) && !hiddenOverlayIds[o.id]
        )
        .sort((a, b) => compareTitles(a.title, b.title)),
    [planMapOverlays, showAuditFixtures, hiddenOverlayIds]
  );
  // Web only, like the overlays above — no native rendering for plan maps
  // to toggle. Hidden for a non-moderator with nothing shared yet; a
  // moderator always sees it (for the Add row, even with none imported).
  const showPlanMaps = Platform.OS === 'web' && (isModerator || visiblePlanMapOverlays.length > 0);
  // The fullscreen presentation (MapSlideshow.web.tsx) is web-only, and the
  // desktop shell already has its own "Present observations" chip over the
  // map — this row would just duplicate it. Phones and tablets have no
  // chip, so they keep the row.
  const showPresent = Platform.OS === 'web' && !isDesktopShell;
  // Viewing plans works everywhere on web ("Shared plans" is a plain list);
  // drawing (Your plans, New plan) is desktop-shell only (B9 — no touch
  // drawing).
  const showPlans = Platform.OS === 'web';

  return (
    <View style={styles.content}>
      <View style={styles.titleRow}>
        <Text style={[styles.title, { color: colors.text }]}>Map layers</Text>
        {showCloseButton && (
          <Pressable
            onPress={onClose}
            testID="map-layers-close"
            accessibilityRole="button"
            accessibilityLabel="Close map layers"
            style={[styles.closeButton, CURSOR_POINTER]}>
            <X size={18} color={colors.textSecondary} />
          </Pressable>
        )}
      </View>

      <Text
        style={[
          styles.sectionLabel,
          styles.sectionLabelSpaced,
          styles.sectionLabelFirst,
          { color: colors.textSecondary },
        ]}>
        Map style
      </Text>
      <View style={styles.section} accessibilityRole="radiogroup">
        {BASEMAP_OPTIONS.map((option) => {
          const selected = mapStyle === option.id;
          return (
            <TouchableOpacity
              key={option.id}
              style={[styles.row, { borderBottomColor: colors.border }]}
              onPress={() => setMapStyle(option.id)}
              activeOpacity={0.7}
              testID={`map-layers-basemap-${option.id}`}
              accessibilityRole="radio"
              // RN-web drops accessibilityState on export, so the raw
              // aria-* prop is what axe and screen readers actually see.
              aria-checked={selected}
              accessibilityLabel={option.label}>
              <option.icon size={18} color={colors.textSecondary} style={styles.rowIcon} />
              <View style={styles.rowText}>
                <Text style={[styles.rowLabel, { color: colors.text }]}>{option.label}</Text>
                <Text style={[styles.rowMeta, { color: colors.textSecondary }]}>
                  {option.description}
                </Text>
              </View>
              {selected && <Check size={20} color={colors.text} />}
            </TouchableOpacity>
          );
        })}
      </View>

      {showOverlays && (
        <>
          <Text
            style={[
              styles.sectionLabel,
              styles.sectionLabelSpaced,
              { color: colors.textSecondary },
            ]}>
            Overlays
          </Text>
          <View style={styles.section}>
            <OverlayRow
              icon={Camera}
              label="Observations"
              source="Community photos and notes"
              checked={observationsVisible}
              onPress={() => setObservationsVisible(!observationsVisible)}
              testID="map-layers-overlay-observations"
            />
            <OverlayRow
              icon={Flame}
              label="Fire hazard zones"
              source={FHSZ_SOURCE_LABEL}
              checked={fhszOverlayEnabled}
              onPress={toggleFhszOverlay}
              testID="map-layers-overlay-fhsz"
            />
            <OverlayRow
              icon={History}
              label="Fire history"
              source={FIRE_HISTORY_SOURCE_LABEL}
              checked={fireHistoryEnabled}
              onPress={toggleFireHistory}
              testID="map-layers-overlay-fire-history"
            />
            {/* Appended rather than slotted in beside the hazard layers:
                these two are recognized communities, not hazard data, and
                appending keeps the visual baseline diff to new rows at the
                bottom. */}
            <OverlayRow
              icon={Award}
              label={COMMUNITY_PROGRAMS.firewise.label}
              source={COMMUNITY_PROGRAMS.firewise.sourceLabel}
              checked={firewiseEnabled}
              onPress={toggleFirewise}
              testID="map-layers-overlay-firewise"
            />
            <OverlayRow
              icon={Users}
              label={COMMUNITY_PROGRAMS['fire-safe-councils'].label}
              source={COMMUNITY_PROGRAMS['fire-safe-councils'].sourceLabel}
              checked={fireSafeCouncilsEnabled}
              onPress={toggleFireSafeCouncils}
              testID="map-layers-overlay-fire-safe-councils"
            />
          </View>
        </>
      )}

      {showPlanMaps && (
        <>
          <Text
            style={[
              styles.sectionLabel,
              styles.sectionLabelSpaced,
              { color: colors.textSecondary },
            ]}>
            Plan maps
          </Text>
          <View style={styles.section} testID="map-layers-plan-maps-section">
            {isModerator && (
              <TouchableOpacity
                style={[styles.row, { borderBottomColor: colors.border }]}
                onPress={handleAddPlanMap}
                activeOpacity={0.7}
                testID="map-layers-add-plan-map"
                accessibilityRole="button"
                accessibilityLabel="Add plan map">
                <ImagePlus size={18} color={colors.textSecondary} style={styles.rowIcon} />
                <View style={styles.rowText}>
                  <Text style={[styles.rowLabel, { color: colors.text }]}>Add plan map</Text>
                  <Text style={[styles.rowMeta, { color: colors.textSecondary }]}>
                    Import a page from a wildfire plan
                  </Text>
                </View>
              </TouchableOpacity>
            )}
            {visiblePlanMapOverlays.map((overlay) => (
              <OverlayRow
                key={overlay.id}
                icon={MapPin}
                label={overlay.title}
                labelNumberOfLines={2}
                source={
                  overlay.note ? `${overlay.source.label} · ${overlay.note}` : overlay.source.label
                }
                checked={enabledOverlayIds.includes(overlay.id)}
                onPress={() => toggleOverlay(overlay.id)}
                testID={`map-layers-plan-map-row-${overlay.id}`}
                tag={overlay.visibility === 'draft' ? 'Draft' : undefined}
                trailingActions={
                  isModerator ? (
                    <View style={styles.rowActions}>
                      <TouchableOpacity
                        onPress={() => handleEditPlanMapPlacement(overlay.id)}
                        testID={`plan-map-edit-${overlay.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={`Edit placement for ${overlay.title}`}
                        style={styles.rowActionButton}>
                        <Pencil size={16} color={colors.textSecondary} />
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => handleRemovePlanMap(overlay)}
                        testID={`plan-map-delete-${overlay.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={`Delete ${overlay.title}`}
                        style={styles.rowActionButton}>
                        <Trash2 size={16} color={colors.danger} />
                      </TouchableOpacity>
                    </View>
                  ) : undefined
                }
              />
            ))}
          </View>
        </>
      )}

      {showPresent && (
        <>
          <Text
            style={[
              styles.sectionLabel,
              styles.sectionLabelSpaced,
              { color: colors.textSecondary },
            ]}>
            Present
          </Text>
          <View style={styles.section}>
            <TouchableOpacity
              style={[styles.row, { borderBottomColor: colors.border }]}
              onPress={handlePresentObservations}
              activeOpacity={0.7}
              testID="map-layers-present-observations"
              accessibilityRole="button"
              accessibilityLabel="Present observations">
              <Presentation size={18} color={colors.textSecondary} style={styles.rowIcon} />
              <View style={styles.rowText}>
                <Text style={[styles.rowLabel, { color: colors.text }]}>Present observations</Text>
                <Text style={[styles.rowMeta, { color: colors.textSecondary }]}>
                  Fullscreen walkthrough of what&apos;s on the map now
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        </>
      )}

      {showPlans && (
        <>
          <Text
            style={[
              styles.sectionLabel,
              styles.sectionLabelSpaced,
              { color: colors.textSecondary },
            ]}>
            Plans
          </Text>
          <View style={styles.section}>
            {/* "Your plans" is drawing, not just viewing — B9, no touch
                drawing, so it's desktop-shell only. A phone/tablet viewer
                goes straight to Shared plans below. */}
            {isDesktopShell && (
              <>
                <Text style={[styles.subsectionLabel, { color: colors.textTertiary }]}>
                  Your plans
                </Text>
                {ownPlans.length === 0 ? (
                  <TouchableOpacity
                    style={[styles.row, { borderBottomColor: colors.border }]}
                    onPress={handleDrawPlan}
                    activeOpacity={0.7}
                    testID="map-layers-plan-draw"
                    accessibilityRole="button"
                    accessibilityLabel="Draw a defense plan">
                    <Shield size={18} color={colors.textSecondary} style={styles.rowIcon} />
                    <View style={styles.rowText}>
                      <Text style={[styles.rowLabel, { color: colors.text }]}>
                        Draw a defense plan
                      </Text>
                      <Text style={[styles.rowMeta, { color: colors.textSecondary }]}>
                        Your strategies, drawn on the map
                      </Text>
                    </View>
                  </TouchableOpacity>
                ) : (
                  <>
                    {ownPlans.map((plan, i) => (
                      <TouchableOpacity
                        key={plan.id}
                        style={[styles.row, { borderBottomColor: colors.border }]}
                        onPress={() => handleOpenOwnPlan(plan.id)}
                        activeOpacity={0.7}
                        // The first row is the most recently updated plan
                        // (ownPlans is already sorted that way) — audits
                        // that open the editor from the sheet in one
                        // click press this testID regardless of how many
                        // plans exist.
                        testID={i === 0 ? 'map-layers-plan-draw' : `map-layers-plan-${plan.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={plan.title}>
                        <Route size={18} color={colors.textSecondary} style={styles.rowIcon} />
                        <View style={styles.rowText}>
                          <Text style={[styles.rowLabel, { color: colors.text }]} numberOfLines={1}>
                            {plan.title}
                          </Text>
                          <Text
                            style={[styles.rowMeta, { color: colors.textSecondary }]}
                            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
                            dataSet={{ visualmask: 'wide' }}>
                            {plan.ownerName} · {plan.featureCount}{' '}
                            {plan.featureCount === 1 ? 'defense' : 'defenses'}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ))}
                    {/* Only when there's already at least one plan to
                        switch away from — with none, "Draw a defense
                        plan" above is the one way to start, and it also
                        has to work while signed out (requireAuth), which
                        this row deliberately doesn't. */}
                    <TouchableOpacity
                      style={[styles.row, { borderBottomColor: colors.border }]}
                      onPress={handleNewPlan}
                      activeOpacity={0.7}
                      testID="map-layers-plan-new"
                      accessibilityRole="button"
                      accessibilityLabel="New plan">
                      <Plus size={18} color={colors.textSecondary} style={styles.rowIcon} />
                      <View style={styles.rowText}>
                        <Text style={[styles.rowLabel, { color: colors.text }]}>New plan</Text>
                        <Text style={[styles.rowMeta, { color: colors.textSecondary }]}>
                          Start with a blank map
                        </Text>
                      </View>
                    </TouchableOpacity>
                  </>
                )}
              </>
            )}

            <Text style={[styles.subsectionLabel, { color: colors.textTertiary }]}>
              Shared plans
            </Text>
            {sharedPlans.length === 0 ? (
              <Text style={[styles.emptyLine, { color: colors.textTertiary }]}>
                No shared plans yet
              </Text>
            ) : (
              sharedPlans.map((plan) => (
                <TouchableOpacity
                  key={plan.id}
                  style={[styles.row, { borderBottomColor: colors.border }]}
                  onPress={() => handleOpenAreaPlan(plan.id)}
                  activeOpacity={0.7}
                  testID={`map-layers-plan-${plan.id}`}
                  accessibilityRole="button"
                  accessibilityLabel={plan.title}>
                  <Route size={18} color={colors.textSecondary} style={styles.rowIcon} />
                  <View style={styles.rowText}>
                    <Text style={[styles.rowLabel, { color: colors.text }]} numberOfLines={1}>
                      {plan.title}
                    </Text>
                    {/* The count moves whenever the owner edits the plan, so
                        the visual audit paints over it. */}
                    <Text
                      style={[styles.rowMeta, { color: colors.textSecondary }]}
                      // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
                      dataSet={{ visualmask: 'wide' }}>
                      {plan.ownerName} · {plan.featureCount}{' '}
                      {plan.featureCount === 1 ? 'defense' : 'defenses'}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))
            )}
          </View>
        </>
      )}

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="map-layers-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

function OverlayRow({
  icon: Icon,
  label,
  labelNumberOfLines = 1,
  source,
  checked,
  onPress,
  testID,
  tag,
  trailingActions,
}: {
  icon: LucideIcon;
  label: string;
  /** Plan map titles are free text and can run long — pass 2 there so a
   *  long one wraps instead of truncating badly. Every other row (a fixed
   *  product label) keeps the default of 1. */
  labelNumberOfLines?: number;
  source: string;
  checked: boolean;
  onPress: () => void;
  testID: string;
  /** Small pill after the label — today only "Draft" on an unshared plan
   *  map, so a moderator can tell which of their overlays no one else sees
   *  yet. Omit for every other row. */
  tag?: string;
  /** Extra icon buttons (edit/delete) after the checkbox, outside the
   *  toggle's own hit area — a moderator's plan-map rows only. Nested
   *  inside the outer row, not the inner Pressable, so they get their own
   *  press instead of also toggling the checkbox underneath them. */
  trailingActions?: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <View style={[styles.row, { borderBottomColor: colors.border }]}>
      <TouchableOpacity
        style={styles.rowToggle}
        onPress={onPress}
        activeOpacity={0.7}
        testID={testID}
        accessibilityRole="checkbox"
        aria-checked={checked}
        accessibilityLabel={label}>
        <Icon size={18} color={colors.textSecondary} style={styles.rowIcon} />
        <View style={styles.rowText}>
          <View style={styles.rowLabelLine}>
            <Text
              style={[styles.rowLabel, { color: colors.text }]}
              numberOfLines={labelNumberOfLines}>
              {label}
            </Text>
            {tag && (
              <View style={[styles.tag, { backgroundColor: colors.hoverSurface }]}>
                <Text style={[styles.tagText, { color: colors.textSecondary }]}>{tag}</Text>
              </View>
            )}
          </View>
          <Text style={[styles.rowMeta, { color: colors.textTertiary }]}>{source}</Text>
        </View>
        <View
          style={[
            styles.checkbox,
            {
              // Ink, not the readiness-green primary — this is an action
              // toggle, not a category signal (matches the Create screen's
              // "Post anonymously" checkbox).
              borderColor: checked ? colors.action : colors.border,
              backgroundColor: checked ? colors.action : 'transparent',
            },
          ]}>
          {checked && <Check size={14} color={colors.actionOn} />}
        </View>
      </TouchableOpacity>
      {trailingActions}
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xl,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SPACING.lg,
  },
  title: {
    ...TYPOGRAPHY.statNumeral,
    letterSpacing: -0.3,
  },
  closeButton: {
    padding: SPACING.xxs,
  },
  // Same rhythm as DefensePlanPanel.web.tsx's sectionLabelSpaced: 24px
  // above a section label, 8px from the label to its first row.
  // sectionLabelSpaced carries both margins; sectionLabelFirst zeroes the
  // top one for the first label on the sheet (Sept 29 2026).
  sectionLabel: {
    ...TYPOGRAPHY.overline,
  },
  sectionLabelSpaced: {
    marginTop: SPACING.lg,
    marginBottom: SPACING.sm,
  },
  sectionLabelFirst: {
    marginTop: 0,
  },
  // Hairline rows, not filled cards — the shape the appearance screen's
  // radio list uses. No border of its own: that would double with the
  // section label's own spacing (row dividers already separate rows).
  section: {},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  // OverlayRow's inner toggle — the outer `row` above still carries the
  // padding/border, so a row with trailingActions (a moderator's plan-map
  // rows) reads at the same height as one without.
  rowToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  rowIcon: {
    marginRight: SPACING.smd,
  },
  rowText: {
    flex: 1,
    paddingRight: SPACING.sm,
  },
  rowLabelLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  rowLabel: {
    ...TYPOGRAPHY.h4,
    fontFamily: TYPOGRAPHY.label.fontFamily,
    fontSize: FONT_SIZES.md,
  },
  rowMeta: {
    ...TYPOGRAPHY.caption,
    marginTop: 2,
  },
  tag: {
    paddingHorizontal: SPACING.xs,
    paddingVertical: 1,
    borderRadius: BORDER_RADIUS.sm,
  },
  tagText: {
    ...TYPOGRAPHY.overline,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: BORDER_RADIUS.xs,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowActions: {
    flexDirection: 'row',
    gap: SPACING.xs,
    marginLeft: SPACING.sm,
  },
  rowActionButton: {
    padding: SPACING.xxs,
  },
  scrollSentinel: {
    height: 1,
  },
  subsectionLabel: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.smd,
    marginBottom: SPACING.xxs,
  },
  emptyLine: {
    ...TYPOGRAPHY.caption,
    paddingVertical: SPACING.sm,
  },
});
